import network from '@/services/network';
import { SoftfairFondsAuswahl, SoftfairFondsliste } from '@/services/api/softfairAdmin';

/**
 * softfair-Daten für den Antrags-Wizard (BER-67) — für alle eingeloggten
 * Nutzer (keine Admin-Permission nötig).
 *
 * Der Beruf wird JE ANTRAG auf der Wizard-Antragsseite gewählt (Pflichtangabe
 * der softfair-Berechnung); die Berufslisten sind gesellschaftsspezifisch.
 */

export interface SoftfairBeruf {
  id: number;
  name: string;
}

/** Eine im Admin konfigurierte softfair-Kombination (Gesellschaft, Tarif, Typ). */
export interface SoftfairKonfiguration {
  /** Eindeutige Konfigurations-ID — unterscheidet Varianten je (Tarif, Typ). */
  id: number;
  gesellschaft_id: number;
  gesellschaft_name: string;
  tarif_id: string;
  tarif_name: string;
  /** Optionaler User-freundlicher Name für den Wizard (leer = Tarifname). */
  anzeigename: string | null;
  /** Antragstyp der Konfiguration (application_types.value). */
  application_type_value: string | null;
  /** Nur sichtbare Konfigurationen erscheinen im Wizard. */
  sichtbar: boolean;
}

/** Berufssuche je softfair-Gesellschaft (min. 3 Zeichen). */
const berufe = async (suchtext: string, gesellschaftId: number): Promise<SoftfairBeruf[]> => {
  const { data } = await network.get<{ data: SoftfairBeruf[] }>('/softfair/berufe', {
    params: { suchtext, gesellschaft_id: gesellschaftId },
  });
  return data.data;
};

/**
 * Konfigurierte softfair-Sätze. Standard: nur sichtbare (Wizard); Admin-
 * Ansichten holen mit alle=true auch die ausgeblendeten.
 */
const konfigurationen = async (alle = false): Promise<SoftfairKonfiguration[]> => {
  const { data } = await network.get<{ data: SoftfairKonfiguration[] }>('/softfair/konfigurationen', {
    params: alle ? { alle: 1 } : undefined,
  });
  return data.data;
};

/** Fonds-Standard + Fondsliste einer (Tarif, Typ)-Kombination (Wizard). */
export interface SoftfairFondsKonfiguration {
  /** Der im Admin gepflegte Fonds-Standard ({isin, name, anteil}, Summe 100). */
  fonds: SoftfairFondsAuswahl[];
  /** Verfügbare Fonds + Auswahlregeln; null = keine freie Liste verfügbar. */
  fondsliste: SoftfairFondsliste | null;
}

/**
 * Fonds-Konfiguration für die Berater-Anpassung im Wizard: Admin-Standard +
 * live geladene softfair-Fondsliste — dieselbe Datenbasis wie im Admin-Panel.
 */
const fondsKonfiguration = async (
  konfigId: number,
): Promise<SoftfairFondsKonfiguration> => {
  const { data } = await network.get<{ data: SoftfairFondsKonfiguration }>(
    '/softfair/fonds-konfiguration',
    { params: { konfig_id: konfigId } },
  );
  return data.data;
};

const softfair = { berufe, konfigurationen, fondsKonfiguration };

export default softfair;
